





























































































































import {
  defineComponent,
  ref,
  useContext,
  useRoute,
  watchEffect,
  nextTick,
  reactive,
  onMounted,
} from '@nuxtjs/composition-api'
import isEmpty from 'lodash/fp/isEmpty'

import CitationCarousel from './CitationCarousel.vue'
import CitationsPagination from './CitationsPagination.vue'
import CitationExtendedView from './CitationExtendedView.vue'
import CitationLine from '@/components/feature/PDP/CitationLine.vue'
import CitationsFilters from '@/components/feature/PDP/CitationsFilters.vue'

import { filterReferencesForManualCitations, generateReferenceUrl } from '@/common/utils/product/product'
import { COMPONENTS_IN_NAVIGATION } from '@/common/utils/product/constants'

import { usePdpStore } from '~/store/pdp'
import { useProductStore } from '~/store/product'
import { CitationResult, CitationImageExtended, CitationsResultsResponse } from '~/types/product/productTypes'
import { usePDPNavigationObserver } from '~/composables/pdp/usePDPNavigationObserver'
import { useVueModal } from '~/composables/ui/useVueModal'

export default defineComponent({
  components: { CitationLine, CitationCarousel, CitationExtendedView, CitationsPagination, CitationsFilters },
  props: {
    confData: {
      type: Object,
      required: true,
    },
    productId: {
      type: String,
      required: true,
    },
    citationsType: {
      type: String,
      default: '',
    },
    references: {
      type: Array as () => any[],
      default: () => [],
    },
  },
  setup({ confData, productId, citationsType, references }) {
    // .. Init stores
    const pdpStore = usePdpStore()
    const productStore = useProductStore()

    // .. references
    const mappedReferences = filterReferencesForManualCitations(references)
    const citationRef = usePDPNavigationObserver(() => {
      pdpStore.setActiveComponentInNavigation(COMPONENTS_IN_NAVIGATION.CITATIONS.ANCHOR)
    })

    // .. Contexts
    const ctx = useContext()
    const route = useRoute()
    const isPreviewServer = ctx.$config.cst.isPreview
    const isManualCitation = citationsType === 'manual'
    const isCstCitationWidget = confData.cstCitationWidget

    // .. Main Storage
    const totalCitations = ref(0)
    const citationResults = ref<{ data: CitationResult[] | null; loading: boolean }>({ data: null, loading: false })
    const citationImages = ref<{ data: CitationImageExtended[] | null; loading: boolean }>({
      data: null,
      loading: false,
    })

    // .. Filters
    const filters = ref<Omit<CitationsResultsResponse['count'], 'total'>>({ applications: {}, reactants: {} })
    const filterValues = reactive<Record<keyof typeof filters.value, string>>({
      applications: 'all',
      reactants: 'all',
    })
    const mainFilter = ref<'applications' | 'reactants' | null>(null)

    // .. Pagination
    const currentPage = ref(1)
    const setCurrentPageAndFetch = (newPage: number) => {
      currentPage.value = newPage

      fetchCitationResults({
        offset: (currentPage.value - 1) * currentPageSize.value,
        limit: currentPageSize.value,
        application: filterValues.applications,
        reactivity: filterValues.reactants,
      })
    }

    const currentPageSize = ref(5)
    const setCurrentPageSizeAndFetch = (newSize: number) => {
      // Set new pageSize
      currentPageSize.value = newSize
      // When we change page size, user should occur on first page
      currentPage.value = 1

      fetchCitationResults({
        offset: (currentPage.value - 1) * currentPageSize.value,
        limit: currentPageSize.value,
        application: filterValues.applications,
        reactivity: filterValues.reactants,
      })
    }

    // .. Fetching data
    const fetchCitationResults = async (params: {
      limit: number
      offset: number
      application: string
      reactivity: string
      changedFilter?: string
    }) => {
      citationResults.value.loading = true

      try {
        const res = await productStore.fetchProductCitationData(productId, {
          limit: params.limit,
          offset: params.offset,
          application: params.application !== 'all' ? params.application : undefined,
          reactants: params.reactivity !== 'all' ? params.reactivity : undefined,
        })

        // .. Parse citations images
        citationResults.value.data = res.citations

        // .. Setting filters
        if (res.count.total) {
          const { total, ...rest } = res.count
          totalCitations.value = total

          if (params.application === 'all' && params.reactivity === 'all') {
            filters.value = rest
            pdpStore.citationsCount = total
          } else {
            filters.value = {
              applications:
                mainFilter.value === 'applications' ||
                (mainFilter.value === 'reactants' && params.changedFilter === 'applications')
                  ? filters.value.applications
                  : Object.keys(filters.value.applications).reduce(
                      (acc, currKey) => ({ ...acc, [currKey]: rest.applications[currKey] || 0 }),
                      {}
                    ),
              reactants:
                mainFilter.value === 'reactants' ||
                (mainFilter.value === 'applications' && params.changedFilter === 'reactants')
                  ? filters.value.reactants
                  : Object.keys(filters.value.reactants).reduce(
                      (acc, currKey) => ({ ...acc, [currKey]: rest.reactants[currKey] || 0 }),
                      {}
                    ),
            }
          }

          pdpStore.setComponentAsRendered(COMPONENTS_IN_NAVIGATION.CITATIONS)
        }

        //
      } catch (error) {
        console.error(error)
      } finally {
        citationResults.value.loading = false
      }
    }

    const fetchCitationsImages = async (params: { application: string; reactivity: string }) => {
      citationImages.value.loading = true

      try {
        const res = await productStore.fetchProductCitationImagesData(productId, {
          application: params.application !== 'all' ? params.application : undefined,
          reactants: params.reactivity !== 'all' ? params.reactivity : undefined,
        })
        // .. Parse citations results + resetting of carousel
        citationImages.value.data = res.images
        setCurrentImage(0)
        setInitialImage(0)

        //
      } catch (error) {
        console.error(error)
      } finally {
        citationImages.value.loading = false
      }
    }

    const fetchResultsAndImages = (changedFilter?: string) => {
      currentPage.value = 1
      fetchCitationResults({
        offset: (currentPage.value - 1) * currentPageSize.value,
        limit: currentPageSize.value,
        application: filterValues.applications,
        reactivity: filterValues.reactants,
        changedFilter,
      })

      citationImages.value.data = null
      fetchCitationsImages({
        application: filterValues.applications,
        reactivity: filterValues.reactants,
      })
    }

    // .. Listen to filter emits
    const changeFilter = (data: { filterKey: keyof typeof filters.value; value: string }) => {
      filterValues[data.filterKey] = data.value

      if (filterValues.applications === 'all' && filterValues.reactants === 'all') {
        mainFilter.value = null
      } else if (filterValues.applications !== 'all' && filterValues.reactants === 'all') {
        mainFilter.value = 'applications'
      } else if (filterValues.applications === 'all' && filterValues.reactants !== 'all') {
        mainFilter.value = 'reactants'
      }

      if (data.filterKey === 'reactants' && mainFilter.value === 'reactants') {
        filterValues.applications = 'all'
      }

      if (data.filterKey === 'applications' && mainFilter.value === 'applications') {
        filterValues.reactants = 'all'
      }
      fetchResultsAndImages(data.filterKey)
    }

    //  .. Listen to clear filter emit
    const clearFilters = () => {
      mainFilter.value = null
      Object.keys(filterValues).forEach((key) => {
        filterValues[key as keyof typeof filterValues] = 'all'
      })
      fetchResultsAndImages()
    }

    // .. Images
    const [initialExtendedImage, setInitialImage] = [ref(0), (newIdx: number) => (initialExtendedImage.value = newIdx)]
    const [currentImage, setCurrentImage] = [ref(0), (newIdx: number) => (currentImage.value = newIdx)]
    const [extendedViewImgs, setExtendedViewImgs] = [
      ref<CitationImageExtended[] | null>(null),
      (newImgs: CitationImageExtended[] | null) => (extendedViewImgs.value = newImgs),
    ]

    // .. Extended view related
    const modal = useVueModal()
    const handleOpenExtendedView = async (staringIdx: number, dataCarousel?: CitationImageExtended[]) => {
      setInitialImage(staringIdx)
      setCurrentImage(staringIdx) // Have to set  it before, so it is passed on initial render
      setExtendedViewImgs(dataCarousel ?? citationImages.value.data) // Will enable rendering

      // .. Wait for modal to render and then show it
      await nextTick()
      modal.show('extendedViewModal')
    }

    const handleCloseExtendedView = () => {
      modal.hide('extendedViewModal')
      setExtendedViewImgs(null)
    }

    // .. Lifecycle Hooks
    onMounted(() => {
      if (isCstCitationWidget && !isManualCitation) {
        fetchResultsAndImages()
      }

      if (isManualCitation && !isEmpty(mappedReferences)) {
        pdpStore.citationsCount = mappedReferences.length
        pdpStore.setComponentAsRendered(COMPONENTS_IN_NAVIGATION.CITATIONS)
      }
    })

    watchEffect(() => {
      if (process.client && route.value.hash.toLowerCase() === '#citations') {
        document.querySelector('#citations')?.scrollIntoView()
      }
    })

    return {
      citationResults,
      totalCitations,
      citationRef,
      isManualCitation,
      isCstCitationWidget,
      generateReferenceUrl,
      pdpStore,
      mappedReferences,
      isPreviewServer,

      // Images
      citationImages,
      initialExtendedImage,
      currentImage,
      setCurrentImage,
      handleOpenExtendedView,
      extendedViewImgs,
      handleCloseExtendedView,

      // Filters
      filters,
      filterValues,
      changeFilter,
      clearFilters,

      // Pagination
      currentPage,
      setCurrentPageAndFetch,
      currentPageSize,
      setCurrentPageSizeAndFetch,
    }
  },
})
